import { ListenErkennung, ProjektListenTyp, SpsImportVorschau } from './Sps';
import { VorbereiteterKiImport } from './vorbereiteter-ki-import.model';

/**
 * Zustand des endgültigen Imports einer Datei im Mehrfachimport.
 *
 *  bereit          noch nicht gesendet
 *  wirdImportiert  Request läuft
 *  erfolgreich     Server hat die Liste angelegt – wird nie erneut gesendet
 *  fehlgeschlagen  Server hat abgelehnt (4xx/5xx mit Antwort) – erneuter Versuch ist sicher
 *  unklar          keine Antwort (Verbindungsabbruch, Timeout, Gateway) – der Server kann
 *                  gespeichert haben; erst nach Prüfung im Projekt erneut senden
 */
export type ImportDateiStatus = 'bereit' | 'wirdImportiert' | 'erfolgreich' | 'fehlgeschlagen' | 'unklar';

/** Ergebnis eines erfolgreichen Imports – für die Zusammenfassung und den Sprung zur Liste. */
export interface ImportDateiErgebnis {
  listeId: number;
  listeName: string;
  eintraege: number;
  /** Nur SPS: Signale, deren Zuordnung der Benutzer noch klären muss. */
  ungeklaert?: number;
  /** Nur SPS: Signale, die der Import automatisch mit vorhandenen Signalen zusammengeführt hat. */
  zusammengefuehrt?: number;
  warnungen: string[];
}

/**
 * Arbeitsstand EINER gewählten Datei im Mehrfachimport.
 *
 * Alles, was der Benutzer je Datei entscheidet, hängt hier – damit Dateien
 * sich beim Wechsel nicht gegenseitig überschreiben und ein Zurückwechseln
 * die vorherigen Einstellungen wieder zeigt.
 */
export interface ImportDatei {
  /** Stabiler Schlüssel für trackBy und die Zuordnung zur eingebetteten KI-Komponente. */
  id: string;

  file: File;

  /** Name + Größe + Änderungsdatum – erkennt dieselbe Datei bei erneuter Auswahl. */
  dateiSchluessel: string;

  // ── Erkennung und Typ ──
  erkennung: ListenErkennung | null;
  erkennungLaeuft: boolean;

  /** Gültiger Typ für den Import – startet mit dem erkannten, vom Benutzer korrigierbar. */
  listenTyp: ProjektListenTyp;

  /** True, sobald der Benutzer den Typ selbst gewählt hat – die Erkennung überschreibt ihn dann nicht mehr. */
  typManuellGesetzt: boolean;

  // ── SPS-Vorschau (Zuordnungsliste, PLC-Tags) ──
  sheetName: string | null;
  name: string | null;
  vorschau: SpsImportVorschau | null;
  vorschauLaeuft: boolean;
  /** 1-basierte Seite der SPS-Vorschau – je Datei, bleibt beim Dateiwechsel erhalten. */
  vorschauSeite: number;
  vorschauSeitengroesse: number;
  /** Laufende Nummer der letzten Vorschau-Anfrage; ältere Antworten werden verworfen. */
  vorschauAnforderung: number;

  // ── KI-Vorschau (Messstellen, Verbraucher) ──
  /** True, sobald die eingebettete KI-Komponente erzeugt wurde; sie bleibt danach erhalten. */
  kiAktiviert: boolean;
  /** Letzte bestätigte Einstellungen der KI-Vorschau – die Grundlage des Imports. */
  kiVorbereitung: VorbereiteterKiImport | null;

  /** Vorschau vom Benutzer bestätigt (Weiter geklickt und Prüfung bestanden). */
  bestaetigt: boolean;

  /** Dateibezogener Fehler (Erkennung, Vorschau). */
  fehler: string | null;

  // ── Endgültiger Import ──
  importStatus: ImportDateiStatus;
  importMeldung: string | null;
  importErgebnis: ImportDateiErgebnis | null;
}

/** Arbeitsstand des gesamten Assistenten (in-memory, überlebt Navigation innerhalb der App). */
export interface ListenImportEntwurf {
  projektId: number;
  projektName: string;
  dateien: ImportDatei[];
  aktuelleDateiIndex: number;
  schritt: 1 | 2 | 3;
  /** Gewählte Quelle der Anlagenteile ('Ort' | 'Path' | null) – gilt für alle SPS-Dateien des Imports. */
  anlagenteilQuelle?: 'Ort' | 'Path' | null;
}

export function dateiSchluesselVon(file: File): string {
  return `${file.name}|${file.size}|${file.lastModified}`;
}

let laufendeDateiId = 0;

export function neueImportDatei(file: File, listenTyp: ProjektListenTyp, typManuellGesetzt: boolean): ImportDatei {
  laufendeDateiId += 1;

  return {
    id: `datei-${Date.now()}-${laufendeDateiId}`,
    file,
    dateiSchluessel: dateiSchluesselVon(file),
    erkennung: null,
    erkennungLaeuft: false,
    listenTyp,
    typManuellGesetzt,
    sheetName: null,
    name: null,
    vorschau: null,
    vorschauLaeuft: false,
    vorschauSeite: 1,
    vorschauSeitengroesse: 25,
    vorschauAnforderung: 0,
    kiAktiviert: false,
    kiVorbereitung: null,
    bestaetigt: false,
    fehler: null,
    importStatus: 'bereit',
    importMeldung: null,
    importErgebnis: null
  };
}
